import React from "react";

import { cn } from "../../lib/utils";
import { getIntegrationSteps } from "../utils";

import type { IntegrationScope } from "../constants";
import type { ModalStep } from "../types/modal";

interface StepIndicatorProps {
  currentStep: ModalStep;
  sourceId: string;
  integrationScope?: IntegrationScope;
  success?: boolean; // Only relevant for "result" step
}

export function StepIndicator({
  currentStep,
  sourceId,
  integrationScope,
  success = true,
}: StepIndicatorProps) {
  const steps = React.useMemo(() => {
    return getIntegrationSteps(sourceId, integrationScope);
  }, [sourceId, integrationScope]);

  const getCurrentStepIndex = () => {
    return steps.findIndex((step) => step.key === currentStep);
  };

  const currentIndex = getCurrentStepIndex();
  const isResultStep = currentStep === "result";
  const isFailedResult = isResultStep && !success;

  return (
    <div className="flex items-start justify-start w-full gap-2.5">
      {steps.map((step, index) => {
        const isActive = index === currentIndex;
        const isCompleted = index < currentIndex;
        const isUpcoming = index > currentIndex;
        const isFailedStep = isFailedResult && index === steps.length - 1; // Last step is failed

        return (
          <React.Fragment key={step.key}>
            <div className="flex items-center justify-start gap-4">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors",
                  {
                    "bg-green-600 text-white":
                      (isActive || isCompleted) && !isFailedStep,
                    "bg-red-600 text-white": isFailedStep,
                    "bg-gray-100 dark:bg-neutral-700 text-zinc-500 dark:text-zinc-400":
                      isUpcoming,
                  },
                )}
              >
                {step.number}
              </div>
              <div
                className={cn("text-sm font-medium transition-colors", {
                  "text-zinc-900 dark:text-white":
                    (isActive || isCompleted) && !isFailedStep,
                  "text-red-600": isFailedStep,
                  "text-zinc-500 dark:text-zinc-400": isUpcoming,
                })}
              >
                {step.label}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className="flex-1 flex items-center justify-center px-0 py-4 min-w-[54px]">
                <div className="h-px w-full bg-zinc-200 dark:bg-neutral-600" />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
